import {BaseFormulaOps} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/BaseFormulaOps';
import {OhmNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmEngine';
import {
    FormulaInputTokenType,
    ICellReferenceExternalFormulaToken,
    ICellReferenceFormulaToken,
    ICellReferenceRangeExternalFormulaToken,
    ICellReferenceRangeFormulaToken,
    IFormulaToken,
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {
    R1C1ColumnOrRowLocation,
    R1C1Location,
    R1C1Range
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/R1C1';
import {MeshLocationUtils} from '@mintdata-wsp/common/util/MeshLocationUtils';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';

export interface IEvalInputOpContext
{
    currentColumnIndex:number;
    currentRowIndex:number;
}

export class FormulaInputEvalOp extends BaseFormulaOps<IEvalInputOpContext>
{
    public constructor()
    {
        super('evalInput');
    }

    formula(formulaStartLiteral:OhmNode, expr:OhmNode, redundantLiteral:OhmNode):IFormulaToken[]
    {
        const type = FormulaInputTokenType.FormulaStart;
        return this.__handleFormulaStart(type, formulaStartLiteral, expr, redundantLiteral);
    }

    lazyFormula(lazyStartLiteral:OhmNode, expr:OhmNode, redundantLiteral:OhmNode):IFormulaToken[]
    {
        const type = FormulaInputTokenType.FormulaLazyStart;
        return this.__handleFormulaStart(type, lazyStartLiteral, expr, redundantLiteral);
    }

    liveFormula(liveStartLiteral:OhmNode, expr:OhmNode, redundantLiteral:OhmNode):IFormulaToken[]
    {
        const type = FormulaInputTokenType.FormulaLiveStart;
        return this.__handleFormulaStart(type, liveStartLiteral, expr, redundantLiteral);
    }

    private __handleFormulaStart(type:FormulaInputTokenType, formulaLiteral:OhmNode, expr:OhmNode, redundantLiteral:OhmNode)
    {
        const formulaStartToken = this.__contents(formulaLiteral);
        const contents = this.__contents(expr);
        mk.debug(area.Formula, `[evalInput] formula start (${type}): ${contents}`);

        return [
            {
                type: type,
                value: formulaStartToken,
                source: formulaLiteral.source,
                startIndex: formulaLiteral.source.startIdx,
                endIndex: formulaLiteral.source.endIdx,
            },
            ...expr.evalInput(),
            ...redundantLiteral.evalInput(),
        ];
    }

    simpleValue(simpleValueliteral:OhmNode):IFormulaToken
    {
        const contents = this.__contents(simpleValueliteral);
        mk.debug(area.Formula, `[evalInput] simpleValue: ${contents}`);

        return {
            type: FormulaInputTokenType.SimpleValue,
            value: contents,
            startIndex: simpleValueliteral.source.startIdx,
            endIndex: simpleValueliteral.source.endIdx,
        };
    }

    functionCall(functionNameLiteral:OhmNode,
                 spaceLiteral:OhmNode,
                 parenthesesStartLiteral:OhmNode,
                 functionArgumentExpr:OhmNode,
                 parenthesesEndLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(
            functionNameLiteral,
            spaceLiteral,
            parenthesesStartLiteral,
            functionArgumentExpr,
            parenthesesEndLiteral,
        );
        mk.debug(area.Formula, `[evalInput] functionCall: ${contents}`);

        const functionName:IFormulaToken = functionNameLiteral.evalInput();
        const space:Array<IFormulaToken> = spaceLiteral.evalInput();
        const parenthesesStart:IFormulaToken = parenthesesStartLiteral.evalInput();
        const functionArgument:Array<IFormulaToken> = functionArgumentExpr.evalInput();
        const parenthesesEnd:Array<IFormulaToken> = parenthesesEndLiteral.evalInput();

        return {
            type: FormulaInputTokenType.FunctionCall,
            value: contents,
            valueParts: {
                functionName,
                space: space && space.length > 0 ? space[0] : null,
                parenthesesStart,
                functionArgument: functionArgument && functionArgument.length > 0 ? functionArgument : null,
                parenthesesEnd: parenthesesEnd && parenthesesEnd.length > 0 ? parenthesesEnd[0] : null,
            },
            startIndex: functionNameLiteral.source.startIdx,
            endIndex: parenthesesEndLiteral.source.endIdx,
        };
    }

    functionArgument(functionArgumentExpr:OhmNode, commaLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(functionArgumentExpr, commaLiteral);
        mk.debug(area.Formula, `[evalInput] functionArgument: ${contents}`);

        const content:Array<IFormulaToken> = functionArgumentExpr.evalInput();
        const comma:Array<IFormulaToken> = commaLiteral.evalInput();

        return {
            type: FormulaInputTokenType.FunctionArgument,
            value: contents,
            valueParts: {
                content: content && content.length > 0 ? content : null,
                comma: comma && comma.length > 0 ? comma[0] : null,
            },
            startIndex: functionArgumentExpr.source.startIdx,
            endIndex: commaLiteral.source.endIdx,
        };
    }

    simpleIdentifier(letter:OhmNode, rigthPart:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(letter, rigthPart);
        mk.debug(area.Formula, `[evalInput] simpleIdentifier: ${contents}`);

        return {
            type: FormulaInputTokenType.SimpleFunctionName,
            value: contents,
            startIndex: letter.source.startIdx,
            endIndex: (rigthPart) ? rigthPart.source.endIdx : letter.source.endIdx,
        };
    }

    qualifiedIdentifier(packageNameExpr:OhmNode, dotExpr:OhmNode, funcNameExpr:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(packageNameExpr, dotExpr, funcNameExpr);
        const packageContents:string = this.__contents(packageNameExpr);
        const dotContents:string = this.__contents(dotExpr);
        const funcNameContents:string = this.__contents(funcNameExpr);
        mk.debug(area.Formula, `[evalInput] qualifiedIdentifier: ${contents}`);

        return {
            type: FormulaInputTokenType.QualifiedFunctionName,
            value: contents,
            valueParts: {
                pkg: packageContents,
                dot: dotContents,
                funcName: funcNameContents,
            },
            startIndex: packageNameExpr.source.startIdx,
            endIndex: funcNameExpr.source.endIdx,
        };
    }

    identifier(identifierLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(identifierLiteral);
        mk.debug(area.Formula, `[evalInput] identifier: ${contents}`);

        return identifierLiteral.evalInput();
    }

    sheetSpecialName(_1:OhmNode, nameLiteral:OhmNode, _2:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(_1, nameLiteral, _2);
        const startContents:string = this.__contents(_1);
        const endContents:string = this.__contents(_2);
        mk.debug(area.Formula, `[evalInput] sheetSpecialName: ${contents}`);

        return {
            type: FormulaInputTokenType.SheetName,
            value: contents,
            valueParts: {
                start: startContents,
                content: this.__contents(nameLiteral),
                end: endContents && endContents.length > 0 ? endContents : null,
            },
            startIndex: _1.source.startIdx,
            endIndex: _2.source.endIdx,
        };
    }

    singleSheetName(nameNode:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(nameNode);
        mk.debug(area.Formula, `[evalInput] singleSheetName: ${contents}`);

        let result = nameNode.evalInput();

        mk.debug(area.Formula, `[evalInput] singleSheetName: ${contents} => ${result}`);
        return result;
    }

    externalCellRef(singleSheetName:OhmNode, _excl:OhmNode, cellRef:OhmNode):ICellReferenceExternalFormulaToken | ICellReferenceRangeExternalFormulaToken
    {
        const contents:string = this.__contents(singleSheetName, _excl, cellRef);
        mk.debug(area.Formula, `[evalInput] externalCellRef: ${contents}`);

        const singleSheetNameFormulaToken = singleSheetName.evalInput() as IFormulaToken;
        const cellReferenceFormulaToken = cellRef.evalInput() as ICellReferenceFormulaToken;

        return cellReferenceFormulaToken.type === FormulaInputTokenType.CellReference
            ? {
                type: FormulaInputTokenType.CellReferenceExternal,
                value: contents,
                location: (cellReferenceFormulaToken as ICellReferenceFormulaToken).location,
                range: cellReferenceFormulaToken.range,
                sheetName: singleSheetNameFormulaToken.value,
                startIndex: singleSheetName.source.startIdx,
                endIndex: cellRef.source.endIdx,
            } as ICellReferenceExternalFormulaToken : {
                type: FormulaInputTokenType.CellReferenceRangeExternal,
                value: contents,
                range: cellReferenceFormulaToken.range,
                sheetName: singleSheetNameFormulaToken.value,
                startIndex: singleSheetName.source.startIdx,
                endIndex: cellRef.source.endIdx,
            } as ICellReferenceRangeExternalFormulaToken;
    }

    // 	cellReferenceRangeR1C1 = (cellReferenceR1C1 colonLiteral (cellReferenceR1C1 | columnReferenceR1C1 | rowReferenceR1C1)?)
    // | (columnReferenceR1C1 colonLiteral (cellReferenceR1C1 | columnReferenceR1C1)?)
    // | (rowReferenceR1C1 colonLiteral (cellReferenceR1C1 | rowReferenceR1C1)?)

    cellReferenceRangeR1C1(startCellReferenceExpr:OhmNode, colonExpr:OhmNode, endCellReferenceExpr:OhmNode):ICellReferenceRangeFormulaToken
    {
        const contents:string = this.__contents(startCellReferenceExpr, colonExpr, endCellReferenceExpr);

        mk.debug(area.Formula, `[evalInput] cellReferenceRangeR1C1: ${contents}`);

        const startCellReference:ICellReferenceFormulaToken = startCellReferenceExpr.evalInput();
        const endCellReferences:Array<ICellReferenceFormulaToken> = endCellReferenceExpr.evalInput();
        const endCellReference:ICellReferenceFormulaToken = endCellReferences && endCellReferences.length > 0
            ? endCellReferences[0]
            : null;

        return {
            type: FormulaInputTokenType.CellReferenceRange,
            value: contents,
            range: new R1C1Range(
                startCellReference.location,
                endCellReference ? endCellReference.location : null,
            ),
            startIndex: startCellReferenceExpr.source.startIdx,
            endIndex: endCellReferenceExpr.source.endIdx,
        };
    }

    // 	cellReferenceR1C1 = "R" columnOrRowReferenceR1C1Literal? "C" columnOrRowReferenceR1C1Literal?

    cellReferenceR1C1(rExpr:OhmNode,
                      rowOffsetExpr:OhmNode,
                      cExpr:OhmNode,
                      columnOffsetExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(
            rExpr,
            rowOffsetExpr,
            cExpr,
            columnOffsetExpr,
        );
        const rowOffsetContents:string = this.__contents(rowOffsetExpr);
        const columnOffsetContents:string = this.__contents(columnOffsetExpr);

        mk.debug(area.Formula, `[evalInput] cellReferenceR1C1: ${contents}`);

        const rowOffsetExprValue:R1C1ColumnOrRowLocation = rowOffsetContents
            ? rowOffsetExpr.children[0].evalInput()
            : new R1C1ColumnOrRowLocation(0, false);
        const columnOffsetExprValue:R1C1ColumnOrRowLocation = columnOffsetContents
            ? columnOffsetExpr.children[0].evalInput()
            : new R1C1ColumnOrRowLocation(0, false);

        const location:R1C1Location = new R1C1Location(
            rowOffsetExprValue.offset,
            rowOffsetExprValue.isFixed,
            columnOffsetExprValue.offset,
            columnOffsetExprValue.isFixed,
        );

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: location,
            range: new R1C1Range(location, location),
            startIndex: rExpr.source.startIdx,
            endIndex: columnOffsetExpr.source.endIdx,
        };
    }

    // 	columnReferenceR1C1 = "C" columnOrRowReferenceR1C1Literal?

    columnReferenceR1C1(cExpr:OhmNode, columnOffsetExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(
            cExpr,
            columnOffsetExpr,
        );

        mk.debug(area.Formula, `[evalInput] columnReferenceR1C1: ${contents}`);

        const columnOffsetContents:string = this.__contents(columnOffsetExpr);
        const columnOffsetExprValue:R1C1ColumnOrRowLocation = columnOffsetContents
            ? columnOffsetExpr.children[0].evalInput()
            : new R1C1ColumnOrRowLocation(0, false);

        const location:R1C1Location = new R1C1Location(
            null,
            false,
            columnOffsetExprValue.offset,
            columnOffsetExprValue.isFixed,
        );

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: location,
            range: new R1C1Range(location, location),
            startIndex: cExpr.source.startIdx,
            endIndex: columnOffsetExpr.source.endIdx,
        };
    }

    // 	rowReferenceR1C1 = "R" columnOrRowReferenceR1C1Literal?

    rowReferenceR1C1(rExpr:OhmNode, rowOffsetExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(
            rExpr,
            rowOffsetExpr
        );

        mk.debug(area.Formula, `[evalInput] rowReferenceR1C1: ${contents}`);

        const rowOffsetContents:string = this.__contents(rowOffsetExpr);
        const rowOffsetExprValue:R1C1ColumnOrRowLocation = rowOffsetContents
            ? rowOffsetExpr.children[0].evalInput()
            : new R1C1ColumnOrRowLocation(0, false);

        const location:R1C1Location = new R1C1Location(
            rowOffsetExprValue.offset,
            rowOffsetExprValue.isFixed,
            null,
            false
        );

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: location,
            range: new R1C1Range(location, location),
            startIndex: rExpr.source.startIdx,
            endIndex: rowOffsetExpr.source.endIdx,
        };
    }

    // columnOrRowReferenceR1C1Literal = columnOrRowReferenceR1C1LiteralRel | columnOrRowReferenceR1C1LiteralAbs
    columnOrRowReferenceR1C1Literal(columnOrRowReferenceExpr:OhmNode):R1C1ColumnOrRowLocation
    {
        const contents:string = this.__contents(columnOrRowReferenceExpr);

        mk.debug(area.Formula, `[evalInput] columnOrRowReferenceR1C1Literal: ${contents}`);

        return columnOrRowReferenceExpr.evalInput();
    }

    // 	columnOrRowReferenceR1C1LiteralRel = "[" "-"? columnOrRowReferenceR1C1LiteralAbs? "]"?
    columnOrRowReferenceR1C1LiteralRel(openRowBracketExpr:OhmNode,
                                       minusExpr:OhmNode,
                                       rowOffsetExpr:OhmNode,
                                       closeRowBracketExpr:OhmNode):R1C1ColumnOrRowLocation
    {
        const contents:string = this.__contents(
            openRowBracketExpr,
            minusExpr,
            rowOffsetExpr,
            closeRowBracketExpr,
        );

        const rowOffsetContents:string = this.__contents(minusExpr, rowOffsetExpr);

        mk.debug(area.Formula, `[evalInput] columnOrRowReferenceR1C1LiteralRel: ${contents}`);

        const rowOffset:number = rowOffsetContents ? parseFloat(rowOffsetContents) : 0;

        const location:R1C1ColumnOrRowLocation = new R1C1ColumnOrRowLocation(rowOffset, false);

        return location;
    }

    // columnOrRowReferenceR1C1LiteralAbs = digit+
    columnOrRowReferenceR1C1LiteralAbs(digits:OhmNode):R1C1ColumnOrRowLocation
    {
        const contents:string = this.__contents(digits);

        mk.debug(area.Formula, `[evalInput] columnOrRowReferenceR1C1LiteralAbs: ${contents}`);

        const offset:number = contents ? parseFloat(contents) : 0;

        const location:R1C1ColumnOrRowLocation = new R1C1ColumnOrRowLocation(offset, true);

        return location;
    }

    cellReferenceRangeA1(startCellReferenceExpr:OhmNode, colonExpr:OhmNode, endCellReferenceExpr:OhmNode):ICellReferenceRangeFormulaToken
    {
        const contents:string = this.__contents(startCellReferenceExpr, colonExpr, endCellReferenceExpr);

        mk.debug(area.Formula, `[evalInput] cellReferenceRangeA1: ${contents}`);

        const startCellReference:ICellReferenceFormulaToken = startCellReferenceExpr.evalInput();
        const endCellReferences:Array<ICellReferenceFormulaToken> = endCellReferenceExpr.evalInput();
        const endCellReference:ICellReferenceFormulaToken = endCellReferences && endCellReferences.length > 0
            ? endCellReferences[0]
            : null;

        return {
            type: FormulaInputTokenType.CellReferenceRange,
            value: contents,
            range: new R1C1Range(
                startCellReference.location,
                endCellReference ? endCellReference.location : null,
            ),
            startIndex: startCellReferenceExpr.source.startIdx,
            endIndex: endCellReferenceExpr.source.endIdx,
        };
    }

    cellReferenceA1(columnDollarSignExpr:OhmNode, columnExpr:OhmNode, rowDollarSignExpr:OhmNode, rowExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(columnDollarSignExpr, columnExpr, rowDollarSignExpr, rowExpr);
        const columnDollarSignContents:string = this.__contents(columnDollarSignExpr);
        const columnContents:string = this.__contents(columnExpr);
        const rowDollarSignContents:string = this.__contents(rowDollarSignExpr);
        const rowContents:string = this.__contents(rowExpr);

        mk.debug(area.Formula, `[evalInput] cellReferenceA1: col=${columnContents} row=${rowContents}`);

        const isColFixed:boolean = !!columnDollarSignContents;
        const isRowFixed:boolean = !!rowDollarSignContents;
        const colIndex:number = MeshLocationUtils.getColumnIndexByLetter(columnContents);
        const rowIndex:number = MeshLocationUtils.getRowIndexByLetter(rowContents);

        const location:R1C1Location = new R1C1Location(
            isRowFixed
                ? rowIndex + 1
                : rowIndex - this._context.currentRowIndex,
            isRowFixed,
            isColFixed
                ? colIndex + 1
                : colIndex - this._context.currentColumnIndex,
            isColFixed,
        );

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: location,
            range: new R1C1Range(location, location),
            startIndex: columnDollarSignExpr.source.startIdx,
            endIndex: rowExpr.source.endIdx,
        };
    }

    columnReferenceA1(dollarSignExpr:OhmNode, columnExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(dollarSignExpr, columnExpr);
        const dollarSignContents:string = this.__contents(dollarSignExpr);
        const columnContents:string = this.__contents(columnExpr);

        mk.debug(area.Formula, `[evalInput] columnReferenceA1: $=${dollarSignContents} col=${columnContents}`);

        const isColFixed:boolean = !!dollarSignContents;
        const colIndex:number = MeshLocationUtils.getColumnIndexByLetter(columnContents);

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: new R1C1Location(
                null,
                false,
                isColFixed
                    ? colIndex + 1
                    : colIndex - this._context.currentColumnIndex,
                isColFixed,
            ),
            range: null,
            startIndex: dollarSignExpr.source.startIdx,
            endIndex: columnExpr.source.endIdx,
        };
    }

    rowReferenceA1(dollarSignExpr:OhmNode, rowExpr:OhmNode):ICellReferenceFormulaToken
    {
        const contents:string = this.__contents(dollarSignExpr, rowExpr);
        const dollarSignContents:string = this.__contents(dollarSignExpr);
        const rowContents:string = this.__contents(rowExpr);

        mk.debug(area.Formula, `[evalInput] rowReferenceA1: $=${dollarSignContents} col=${rowContents}`);

        const isRowFixed:boolean = !!dollarSignContents;
        const rowIndex:number = MeshLocationUtils.getRowIndexByLetter(rowContents);

        return {
            type: FormulaInputTokenType.CellReference,
            value: contents,
            location: new R1C1Location(
                isRowFixed
                    ? rowIndex + 1
                    : rowIndex - this._context.currentRowIndex,
                isRowFixed,
                null,
                false,
            ),
            range: null,
            startIndex: dollarSignExpr.source.startIdx,
            endIndex: rowExpr.source.endIdx,
        };
    }

    operand(operandLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(operandLiteral);
        mk.debug(area.Formula, `[evalInput] operand: ${contents}`);

        return {
            type: FormulaInputTokenType.Operand,
            value: contents,
            startIndex: operandLiteral.source.startIdx,
            endIndex: operandLiteral.source.endIdx,
        };
    }

    parentheses(parenthesesStartLiteral:OhmNode, expr:OhmNode, parenthesesEndLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(parenthesesStartLiteral, expr, parenthesesEndLiteral);
        mk.debug(area.Formula, `[evalInput] parentheses: ${contents}`);

        const start:IFormulaToken = parenthesesStartLiteral.evalInput();
        const content:Array<IFormulaToken> = expr.evalInput();
        const end:Array<IFormulaToken> = parenthesesEndLiteral.evalInput();

        return {
            type: FormulaInputTokenType.Parentheses,
            value: contents,
            valueParts: {
                start,
                content,
                end: end && end.length > 0 ? end[0] : null,
            },
            startIndex: parenthesesStartLiteral.source.startIdx,
            endIndex: parenthesesEndLiteral.source.endIdx,
        };
    }

    parenthesesStart(parenthesesStartLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(parenthesesStartLiteral);
        mk.debug(area.Formula, `[evalInput] parenthesesStart: ${contents}`);

        return {
            type: FormulaInputTokenType.ParenthesesStart,
            value: contents,
            startIndex: parenthesesStartLiteral.source.startIdx,
            endIndex: parenthesesStartLiteral.source.endIdx,
        };
    }

    parenthesesEnd(parenthesesEndLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(parenthesesEndLiteral);
        mk.debug(area.Formula, `[evalInput] parenthesesEnd: ${contents}`);

        return {
            type: FormulaInputTokenType.ParenthesesEnd,
            value: contents,
            startIndex: parenthesesEndLiteral.source.startIdx,
            endIndex: parenthesesEndLiteral.source.endIdx,
        };
    }

    spaceLiteral(spaceLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(spaceLiteral);
        mk.debug(area.Formula, `[evalInput] spaceLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Space,
            value: contents,
            startIndex: spaceLiteral.source.startIdx,
            endIndex: spaceLiteral.source.endIdx,
        };
    }

    commaLiteral(commaLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(commaLiteral);
        mk.debug(area.Formula, `[evalInput] commaLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Comma,
            value: contents,
            startIndex: commaLiteral.source.startIdx,
            endIndex: commaLiteral.source.endIdx,
        };
    }

    unknownLiteral(unknownLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(unknownLiteral);
        mk.debug(area.Formula, `[evalInput] unknownLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Unknown,
            value: contents,
            startIndex: unknownLiteral.source.startIdx,
            endIndex: unknownLiteral.source.endIdx,
        };
    }

    inputFrameColumnReference(_1:OhmNode, identifier:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(_1, identifier);
        mk.debug(area.Formula, `[evalInput] frameColumnReference: ${contents}`);

        return {
            type: FormulaInputTokenType.SheetName,
            value: contents,
            startIndex: _1.source.startIdx,
            endIndex: identifier.source.endIdx,
        };
    }

    redundantLiteral(redundantLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(redundantLiteral);
        mk.debug(area.Formula, `[evalInput] redundantLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Redundant,
            value: contents,
            startIndex: redundantLiteral.source.startIdx,
            endIndex: redundantLiteral.source.endIdx,
        };
    }

    inputStringLiteral(formulaStringStart:OhmNode, formulaStringLiteral:OhmNode, formulaStringEnd:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(formulaStringStart, formulaStringLiteral, formulaStringEnd);
        const startContents:string = this.__contents(formulaStringStart);
        const endContents:string = this.__contents(formulaStringEnd);
        mk.debug(area.Formula, `[evalInput] stringLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.FormulaString,
            value: contents,
            valueParts: {
                start: startContents,
                content: this.__contents(formulaStringLiteral),
                end: endContents && endContents.length > 0 ? endContents : null,
            },
            startIndex: formulaStringStart.source.startIdx,
            endIndex: formulaStringEnd.source.endIdx,
        };
    }

    decimalLiteral(decimalLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(decimalLiteral);
        mk.debug(area.Formula, `[evalInput] decimalLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Decimal,
            value: contents,
            startIndex: decimalLiteral.source.startIdx,
            endIndex: decimalLiteral.source.endIdx,
        };
    }

    numberLiteral(decimalLiteral:OhmNode, exponent_part:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(decimalLiteral, exponent_part);
        mk.debug(area.Formula, `[evalInput] decimalLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Decimal,
            value: contents,
            startIndex: decimalLiteral.source.startIdx,
            endIndex: (exponent_part) ? exponent_part.source.endIdx : decimalLiteral.source.endIdx,
        };
    }

    integerLiteral(integerLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(integerLiteral);
        mk.debug(area.Formula, `[evalInput] integerLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Integer,
            value: contents,
            startIndex: integerLiteral.source.startIdx,
            endIndex: integerLiteral.source.endIdx,
        };
    }

    boolean(booleanLiteral:OhmNode):IFormulaToken
    {
        const contents:string = this.__contents(booleanLiteral);
        mk.debug(area.Formula, `[evalInput] booleanLiteral: ${contents}`);

        return {
            type: FormulaInputTokenType.Boolean,
            value: contents,
            startIndex: booleanLiteral.source.startIdx,
            endIndex: booleanLiteral.source.endIdx,
        };
    }

    _terminal()
    {
        // do nothing
    }
}
